import React, { useEffect, useMemo, useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Dialog,
    DialogContent,
    Divider,
    IconButton,
    Tooltip,
    Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import toast from "react-hot-toast";

import { PublicProject, PublicProjectFile } from "../OrderPublicPage";
import { BRAND_PROJECT_STATUSES, BRAND_PROJECT_STATUS_STYLES, filterRealFiles } from "../../../videoProduction/helper";
import ProjectStatusChip from "../../../videoProduction/components/ProjectStatusChip";
import { PublicBriefSection } from "./PublicBriefSection";
import { PublicExpertSelectionSection } from "./PublicExpertSelectionSection";
import { PublicVideoReviewSection } from "./PublicVideoReviewSection";
import { PublicProjectStatusSection, getStatusMessage } from "./PublicProjectStatusSection";
import { VideoCard } from "../../../../components/videoSection/VideoCard";
import { ProjectDownloadBtn } from "../../../videoProduction/components/ProjectDownloadBtn";
import { ViewToggle } from "../../../../components/ViewToggle/ViewToggle";
import { fileToIVideo, getWebmUrl } from "../../../../utils/downloadHelpers";
import { useChannelGroups } from "../../../../hooks/useChannelGroups";
import { ChannelAccordion } from "../../../videoProduction/components/shared/ChannelAccordion";
import { PublicShare, PublicShareContext } from "../../../../components/PublicShare/PublicShareContext";

interface Props {
    project: PublicProject;
    token: string;
    onRefresh: () => void;
    defaultExpanded?: boolean;
    allowedFileTypes?: Set<string> | null;
    publicBase?: string;
}

// Adapt a PublicProjectFile to the shape fileToIVideo + VideoCard expect.
// Uses downloadUrl as the location so playback and download work correctly.
function adaptFile(file: PublicProjectFile) {
    return fileToIVideo({ ...file, location: file.downloadUrl || file.location, final: file.approved });
}

function getStatusProp(status: string, prop: string): string | null {
    const statusKey = Object.keys(BRAND_PROJECT_STATUSES).find((key) => BRAND_PROJECT_STATUSES[key] === status);
    return statusKey ? BRAND_PROJECT_STATUS_STYLES[statusKey]?.[prop] ?? null : null;
}

interface VideoSectionProps {
    project: PublicProject;
}

function VideoCardGrid({
    files,
    view,
    project,
    onSelect
}: {
    files: PublicProjectFile[];
    view: "grid" | "list";
    project: PublicProject;
    onSelect: (file: PublicProjectFile) => void;
}) {
    return (
        <Box
            className={
                view === "list"
                    ? "flex flex-col gap-3"
                    : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
            }
        >
            {files.map((file) => (
                <VideoCard
                    key={file.id}
                    video={adaptFile(file)}
                    variant={view === "list" ? "row" : "share"}
                    isShare
                    onOpen={() => {
                        if (file.downloadUrl || file.location) onSelect(file);
                    }}
                    project={{ id: project.id, title: project.title, status: project.status, download_page: true } as any}
                />
            ))}
        </Box>
    );
}

function VideoSection({ project }: VideoSectionProps) {
    const [view, setView] = useState<"grid" | "list">("list");
    const [selectedFile, setSelectedFile] = useState<PublicProjectFile | null>(null);
    const [videoErrorShown, setVideoErrorShown] = useState(false);

    const files = filterRealFiles(project.projectFiles);
    const { channelGroups, hasChannels } = useChannelGroups(project.content_channels || [], files);

    if (!files.length && !hasChannels) return null;

    const handleSelect = (file: PublicProjectFile) => {
        setSelectedFile(file);
        setVideoErrorShown(false);
    };

    return (
        <>
            <Box className="flex flex-col gap-4 pt-2">
                {/* Header: video count + view toggle */}
                <Box className="flex items-center justify-between">
                    <Box className="flex items-center gap-3">
                        <Typography variant="h6" fontWeight={600} sx={{ fontSize: 20 }}>
                            Videos
                        </Typography>
                        <Typography variant="body2" color="textSecondary" className="mt-0.5">
                            {files.length} video{files.length !== 1 ? "s" : ""}
                        </Typography>
                    </Box>
                    <ViewToggle
                        view={view}
                        onViewChange={(v) => setView(v as "grid" | "list")}
                        gridValue="grid"
                        listValue="list"
                    />
                </Box>

                {hasChannels ? (
                    <Box className="flex flex-col gap-0">
                        <Box className="flex gap-0 mb-4">
                            <Typography
                                className="text-text-2 shrink-0 whitespace-nowrap"
                                variant="body2"
                                sx={{ pr: "20px" }}
                            >
                                {channelGroups.length} Channel{channelGroups.length !== 1 ? "s" : ""} / {files.length}{" "}
                                Video{files.length !== 1 ? "s" : ""}
                            </Typography>
                            <div className="border-t w-full border-border-token mt-3" />
                        </Box>
                        {channelGroups.map((group, idx) => (
                            <ChannelAccordion key={group.key} group={group} defaultExpanded={idx === 0}>
                                <VideoCardGrid
                                    files={group.videos}
                                    view={view}
                                    project={project}
                                    onSelect={handleSelect}
                                />
                            </ChannelAccordion>
                        ))}
                    </Box>
                ) : (
                    <>
                        <Box className="flex gap-0">
                            <Typography className="w-[110px] text-text-2" variant="body2">
                                {files.length} Videos
                            </Typography>
                            <div className="mt-3 border-t w-full border-border-token" />
                        </Box>
                        <VideoCardGrid files={files} view={view} project={project} onSelect={handleSelect} />
                    </>
                )}
            </Box>

            {/* Click-to-play dialog */}
            <Dialog open={!!selectedFile} onClose={() => setSelectedFile(null)} maxWidth="lg" fullWidth>
                <DialogContent sx={{ p: 0, position: "relative" }}>
                    <IconButton
                        onClick={() => setSelectedFile(null)}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                            zIndex: 1,
                            backgroundColor: "rgba(0,0,0,0.5)",
                            color: "white",
                            "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {selectedFile && (
                        <Box sx={{ width: "100%", backgroundColor: "#000" }}>
                            {(() => {
                                const loc = selectedFile.downloadUrl || selectedFile.location || "";
                                const webmUrl = getWebmUrl(loc);
                                return (
                                    <video
                                        controls
                                        autoPlay
                                        playsInline
                                        preload="metadata"
                                        style={{ width: "100%", height: "auto", display: "block" }}
                                        crossOrigin="anonymous"
                                        onError={(e: any) => {
                                            const vid = e.target as HTMLVideoElement;
                                            if (
                                                vid.error?.code === vid.error?.MEDIA_ERR_SRC_NOT_SUPPORTED &&
                                                vid.readyState === 0 &&
                                                !videoErrorShown
                                            ) {
                                                setVideoErrorShown(true);
                                                toast.error("Failed to load video. Please try downloading it instead.");
                                            } else if (
                                                vid.error?.code === vid.error?.MEDIA_ERR_NETWORK &&
                                                !videoErrorShown
                                            ) {
                                                setVideoErrorShown(true);
                                                toast.error("Network error loading video. Please try again.");
                                            }
                                        }}
                                        onLoadStart={() => setVideoErrorShown(false)}
                                        onCanPlay={() => setVideoErrorShown(false)}
                                        onLoadedData={() => setVideoErrorShown(false)}
                                        onPlaying={() => setVideoErrorShown(false)}
                                    >
                                        {loc && (
                                            <source
                                                src={loc}
                                                type={`video/${loc.substring(loc.lastIndexOf(".") + 1) || "mp4"}`}
                                            />
                                        )}
                                        {webmUrl && webmUrl !== loc && <source src={webmUrl} type="video/webm" />}
                                        Your browser does not support the video tag.
                                    </video>
                                );
                            })()}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}

const VIDEO_VISIBLE_STATUSES = [
    // BRAND_PROJECT_STATUSES.VERSION_REVIEW_NEEDED,
    BRAND_PROJECT_STATUSES.FINAL_DELIVERED
];

export function ProjectPublicPanel({
    project,
    token,
    onRefresh,
    defaultExpanded = false,
    allowedFileTypes = null,
    publicBase = "brand-orders",
}: Props) {
    const realFiles = filterRealFiles(project.projectFiles);
    const hasFiles = realFiles.length > 0;
    const fileCount = realFiles.length;
    const showVideos = VIDEO_VISIBLE_STATUSES.includes(project.status) && hasFiles;

    const storageKey = `publicOrderAccordion:${token}:${project.id}`;
    const publicShare = useMemo<PublicShare>(
        () => ({ token, base: publicBase === "brand-projects" ? "brand-projects" : "brand-orders", projectId: project.id }),
        [token, publicBase, project.id],
    );
    const [expanded, setExpanded] = useState<boolean>(() => {
        try {
            const stored = window.localStorage.getItem(storageKey);
            if (stored !== null) return stored === "true";
        } catch {
            // localStorage unavailable (private mode, etc.) — fall through
        }
        return defaultExpanded;
    });

    useEffect(() => {
        try {
            window.localStorage.setItem(storageKey, expanded ? "true" : "false");
        } catch {
            // ignore quota / privacy errors
        }
    }, [storageKey, expanded]);

    const renderStatusContent = () => {
        switch (project.status) {
            case BRAND_PROJECT_STATUSES.BRIEF_PENDING:
                return <PublicBriefSection project={project} token={token} onRefresh={onRefresh} publicBase={publicBase} />;

            case BRAND_PROJECT_STATUSES.BRIEF_REVIEW_NEEDED:
                return <PublicProjectStatusSection project={project} />;

            case BRAND_PROJECT_STATUSES.BRIEF_DENIED: {
                const { title, body } = getStatusMessage(project.status);
                return (
                    <Box className="flex flex-col gap-4">
                        <Box
                            className="flex flex-col gap-1 p-4 rounded-xl border border-border-token"
                            sx={{ backgroundColor: "var(--surface-2)" }}
                        >
                            <Typography variant="body2" fontWeight={600} color="textPrimary">
                                {title}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {body}
                            </Typography>
                        </Box>
                        {project.brief?.denied_reason && (
                            <Box className="p-3 bg-down-bg border border-[#EB3E33] rounded-md">
                                <Typography variant="body2" color="error" fontWeight={600}>
                                    Denial Reason
                                </Typography>
                                <Typography variant="body2" color="error">
                                    {project.brief.denied_reason}
                                </Typography>
                            </Box>
                        )}
                        <PublicBriefSection project={project} token={token} onRefresh={onRefresh} publicBase={publicBase} />
                    </Box>
                );
            }

            case BRAND_PROJECT_STATUSES.EXPERTS_SELECTION_NEEDED:
                return <PublicExpertSelectionSection project={project} token={token} onRefresh={onRefresh} publicBase={publicBase} />;

            case BRAND_PROJECT_STATUSES.VERSION_REVIEW_NEEDED:
                return <PublicVideoReviewSection
                        project={project}
                        token={token}
                        onRefresh={onRefresh}
                        publicBase={publicBase}
                        allowedFileTypes={allowedFileTypes}
                    />;

            // Feedback submitted → back in Editing. Keep the review section so the
            // client can re-watch the version they commented on; the provider
            // derives a read-only mode from the status (no commenting, no
            // re-submit) and explains why. A first-pass edit with nothing
            // delivered yet still gets the plain status callout.
            case BRAND_PROJECT_STATUSES.EDITING:
                if (!hasFiles) return <PublicProjectStatusSection project={project} />;
                return <PublicVideoReviewSection
                        project={project}
                        token={token}
                        onRefresh={onRefresh}
                        publicBase={publicBase}
                        allowedFileTypes={allowedFileTypes}
                    />;

            default:
                return <PublicProjectStatusSection project={project} />;
        }
    };

    // Shared components (comments download) reach this project through the
    // share link, not the logged-in routes.
    return (
        <PublicShareContext.Provider value={publicShare}>
        <Accordion
            expanded={expanded}
            onChange={(_, isExpanded) => setExpanded(isExpanded)}
            disableGutters
            elevation={0}
            sx={{
                border: "1px solid var(--border)",
                borderRadius: "12px !important",
                "&:before": { display: "none" }
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                    px: 3,
                    py: 1.5,
                    "& .MuiAccordionSummary-content": { alignItems: "center", my: 1 }
                }}
            >
                <Box className="flex items-center justify-between w-full pr-2">
                    <Box className="flex items-center gap-3">
                        <Typography variant="subtitle1" fontWeight={600}>
                            {project.title}
                        </Typography>
                        {showVideos && fileCount > 0 && (
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: 12 }}>
                                {fileCount} video{fileCount !== 1 ? "s" : ""}
                            </Typography>
                        )}
                    </Box>
                    <Box className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                        {publicBase === "brand-orders" && project.public_page_token && (
                            <Tooltip title="Open project share page" arrow placement="top">
                                <a
                                    href={`/projects/public/${project.public_page_token}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center justify-center w-8 h-8 rounded-lg border border-border-token text-text-2 hover:bg-surface-2 transition-colors"
                                >
                                    <OpenInNewIcon sx={{ fontSize: 16 }} />
                                </a>
                            </Tooltip>
                        )}
                        {showVideos && (
                            <ProjectDownloadBtn
                                small
                                projectId={project.id}
                                projectTitle={project.title}
                                files={realFiles.map(adaptFile)}
                                channels={project.content_channels}
                                tooltipTitle="Download All Videos"
                                allowUnapproved={!!(project as any).brand?.pre_approval_downloading}
                                allowedFileTypes={allowedFileTypes}
                            />
                        )}
                        <ProjectStatusChip
                            background={getStatusProp(project.status, "color") || "#F2EBE2"}
                            text={project.status}
                            textColor={getStatusProp(project.status, "textColor") || "#AB854A"}
                            icon={getStatusProp(project.status, "icon")}
                        />
                    </Box>
                </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
                <Divider sx={{ mb: 3 }} />
                {renderStatusContent()}
                {showVideos && <VideoSection project={project} />}
            </AccordionDetails>
        </Accordion>
        </PublicShareContext.Provider>
    );
}
